import * as Yup from 'yup';
import {
    OrbitDocumentTypeEnum,
    OrbitPersonTypeEnum,
} from '../constants/citizenCatalogs';

export interface OrbitCitizenInfoValues {
    personType: string;
    typeDocument: string;
    document: string;
    name: string;
    lastName: string;
    email: string;
    alternateEmail: string;
    cellPhone: string;
    phone: string;
    gender: string;
    isLGBTIQ: boolean;
}

export const initialValues: OrbitCitizenInfoValues = {
    personType: '',
    typeDocument: '',
    document: '',
    name: '',
    lastName: '',
    email: '',
    alternateEmail: '',
    cellPhone: '',
    phone: '',
    gender: '',
    isLGBTIQ: false,
};

export const isJuridicalPersonType = (personType: string) =>
    personType === OrbitPersonTypeEnum.Juridica;

export const isCcDocumentType = (typeDocument: string) =>
    typeDocument === OrbitDocumentTypeEnum.CC;

const hasValue = (value?: string) => Boolean(value?.trim());

const ORBIT_EMAIL_REGEX =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const isValidOrbitEmail = (value?: string) => {
    if (!hasValue(value)) return false;

    return ORBIT_EMAIL_REGEX.test(String(value).trim().toLowerCase());
};

export const validationSchema = Yup.object({
    personType: Yup.string().required('El tipo de persona es obligatorio'),
    typeDocument: Yup.string().required('El tipo de documento es obligatorio'),
    document: Yup.string()
        .required('El número de documento es obligatorio')
        .max(15, 'Máximo 15 caracteres')
        .matches(
            /^[a-zA-Z0-9]*$/,
            'El documento solo debe contener caracteres alfanuméricos'
        ),
    name: Yup.string().when('personType', {
        is: OrbitPersonTypeEnum.Juridica,
        then: (schema) =>
            schema
                .trim()
                .required('El nombre de la empresa es obligatorio')
                .max(50, 'Máximo 50 caracteres')
                .matches(
                    /^[a-zA-Z0-9\s.&-]*$/,
                    'El nombre de la empresa contiene caracteres no permitidos'
                ),
        otherwise: (schema) =>
            schema
                .trim()
                .required('El nombre es obligatorio')
                .max(25, 'Máximo 25 caracteres')
                .matches(
                    /^[a-zA-Z\s]*$/,
                    'El nombre solo debe contener caracteres alfabéticos'
                ),
    }),
    lastName: Yup.string().when('personType', {
        is: OrbitPersonTypeEnum.Juridica,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) =>
            schema
                .trim()
                .required('Los apellidos son obligatorios')
                .max(25, 'Máximo 25 caracteres')
                .matches(
                    /^[a-zA-Z\s]*$/,
                    'Los apellidos solo deben contener caracteres alfabéticos'
                ),
    }),
    email: Yup.string()
        .trim()
        .required('El correo electrónico es obligatorio')
        .max(50, 'Máximo 50 caracteres')
        .test('email-format', 'El correo electrónico es inválido', (value) =>
            isValidOrbitEmail(value)
        ),
    alternateEmail: Yup.string()
        .trim()
        .max(50, 'Máximo 50 caracteres')
        .test(
            'alternate-email-format',
            'El correo electrónico es inválido',
            (value) => !hasValue(value) || isValidOrbitEmail(value)
        ),
    cellPhone: Yup.string()
        .max(15, 'Máximo 15 caracteres')
        .test(
            'cellPhone-chars',
            'El celular contiene caracteres no permitidos',
            (value) => {
                if (!hasValue(value)) return true;
                return /^[+0-9()\s-]*$/.test(value!.trim());
            }
        ),
    phone: Yup.string()
        .max(15, 'Máximo 15 caracteres')
        .test(
            'phone-chars',
            'El teléfono solo debe contener números',
            (value) => {
                if (!hasValue(value)) return true;
                return /^[0-9()\s-]*$/.test(value!.trim());
            }
        ),
    gender: Yup.string().when('personType', {
        is: OrbitPersonTypeEnum.Juridica,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.notRequired(),
    }),
    isLGBTIQ: Yup.boolean(),
});
